/**
 * Admin — Module Create/Edit Form (/admin/modules/new, /admin/modules/:id/edit)
 */

import { useEffect, useState } from 'react';
import { Link, useLocation, useRoute } from 'wouter';
import { AppLayout } from '@/layouts/AppLayout';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correctIndex: number;
}

interface ModuleFormState {
  id: string;
  title: string;
  category: string;
  icon: string;
  videoUrl: string;
  podcastUrl: string;
  hours: string;
  description: string;
  studyGuide: string;
  published: boolean;
  sortOrder: string;
}

const EMPTY_FORM: ModuleFormState = {
  id: '',
  title: '',
  category: 'Core',
  icon: 'school',
  videoUrl: '',
  podcastUrl: '',
  hours: '1',
  description: '',
  studyGuide: '',
  published: false,
  sortOrder: '0',
};

function newQuestion(index: number): QuizQuestion {
  return { id: `q${index + 1}`, question: '', options: ['', '', '', ''], correctIndex: 0 };
}

function AdminModuleFormContent({ editId }: { editId?: string }): React.ReactElement {
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const [form, setForm] = useState<ModuleFormState>(EMPTY_FORM);
  const [quiz, setQuiz] = useState<QuizQuestion[]>([newQuestion(0)]);
  const [loading, setLoading] = useState(Boolean(editId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<string[]>([]);

  useEffect(() => {
    if (user && !user.isAdmin) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    void api.get<{ categories: string[] }>('/api/content/categories')
      .then(r => { setCategories(r.categories); })
      .catch(() => {});
  }, []);

  // Load existing module when editing
  useEffect(() => {
    if (!editId) return;
    void (async () => {
      try {
        const data = await api.get<{
          id: string; title: string; category: string; icon: string;
          videoUrl: string; podcastUrl: string; hours: number;
          description: string; studyGuide: string; quiz: QuizQuestion[];
          published: boolean; sortOrder: number;
        }>(`/api/content/modules/${editId}`);
        setForm({
          id: data.id,
          title: data.title,
          category: data.category,
          icon: data.icon,
          videoUrl: data.videoUrl,
          podcastUrl: data.podcastUrl,
          hours: String(data.hours),
          description: data.description,
          studyGuide: data.studyGuide,
          published: data.published,
          sortOrder: String(data.sortOrder),
        });
        setQuiz(data.quiz.length > 0 ? data.quiz : [newQuestion(0)]);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return;
        setError(err instanceof Error ? err.message : 'Failed to load module.');
      } finally {
        setLoading(false);
      }
    })();
  }, [editId]);

  const setField = (field: keyof ModuleFormState, value: string | boolean): void => {
    setForm(f => ({ ...f, [field]: value }));
  };

  const addQuestion = (): void => {
    setQuiz(q => [...q, newQuestion(q.length)]);
  };

  const removeQuestion = (idx: number): void => {
    setQuiz(q => q.filter((_, i) => i !== idx));
  };

  const updateQuestion = (idx: number, field: keyof QuizQuestion, value: string | number): void => {
    setQuiz(q => q.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const updateOption = (qIdx: number, optIdx: number, value: string): void => {
    setQuiz(q => q.map((item, i) => {
      if (i !== qIdx) return item;
      const options = [...item.options];
      options[optIdx] = value;
      return { ...item, options };
    }));
  };

  const save = async (): Promise<void> => {
    if (!form.title.trim()) { setError('Title is required.'); return; }
    if (!editId && !form.id.trim()) { setError('Module ID is required.'); return; }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        id: form.id.trim(),
        title: form.title.trim(),
        category: form.category,
        icon: form.icon.trim() || 'school',
        videoUrl: form.videoUrl.trim(),
        podcastUrl: form.podcastUrl.trim(),
        hours: parseFloat(form.hours) || 1,
        description: form.description.trim(),
        studyGuide: form.studyGuide,
        quiz,
        published: form.published,
        sortOrder: parseInt(form.sortOrder, 10) || 0,
      };

      if (editId) {
        await api.put(`/api/content/modules/${editId}`, payload);
      } else {
        await api.post('/api/content/modules', payload);
      }
      navigate('/admin/modules');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save module.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <span className="material-symbols-outlined animate-spin text-[var(--stitch-primary)] text-4xl">progress_activity</span>
      </div>
    );
  }

  return (
    <div className="stitch-font-body text-[var(--stitch-on-surface)] max-w-3xl">
      <div className="mb-6">
        <Link href="/admin/modules" className="stitch-font-label text-[13px] text-[var(--stitch-primary)] no-underline inline-flex items-center gap-1">
          <span className="material-symbols-outlined text-[18px]">arrow_back</span> All modules
        </Link>
      </div>

      <h1 className="stitch-font-display text-[30px] md:text-[34px] font-bold text-[var(--stitch-primary)] tracking-tight mb-8">
        {editId ? 'Edit module' : 'New module'}
      </h1>

      {error && (
        <div role="alert" className="mb-6 rounded-lg bg-[var(--color-error-100,#fee2e2)] px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="space-y-6">
        {/* ID — only editable on create */}
        {!editId && (
          <Field label="Module ID (slug)" hint="Lowercase, hyphens only. Cannot be changed after creation.">
            <input
              type="text"
              value={form.id}
              onChange={e => { setField('id', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')); }}
              placeholder="e.g. lumbar-disc-rehab"
              className={inputCls}
            />
          </Field>
        )}

        <Field label="Title">
          <input type="text" value={form.title} onChange={e => { setField('title', e.target.value); }} className={inputCls} />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Category">
            <input
              list="category-options"
              value={form.category}
              onChange={e => { setField('category', e.target.value); }}
              placeholder="e.g. Core"
              className={inputCls}
            />
            <datalist id="category-options">
              {categories.map(c => <option key={c} value={c} />)}
            </datalist>
          </Field>
          <Field label="CPD Hours">
            <input type="number" step="0.5" min="0.5" value={form.hours} onChange={e => { setField('hours', e.target.value); }} className={inputCls} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Icon (Material Symbols name)">
            <input type="text" value={form.icon} onChange={e => { setField('icon', e.target.value); }} placeholder="school" className={inputCls} />
          </Field>
          <Field label="Sort order">
            <input type="number" min="0" value={form.sortOrder} onChange={e => { setField('sortOrder', e.target.value); }} className={inputCls} />
          </Field>
        </div>

        <Field label="Video URL (YouTube embed)" hint="e.g. https://www.youtube.com/embed/VIDEO_ID">
          <input type="url" value={form.videoUrl} onChange={e => { setField('videoUrl', e.target.value); }} className={inputCls} />
        </Field>

        <Field label="Description (shown on card)">
          <textarea rows={2} value={form.description} onChange={e => { setField('description', e.target.value); }} className={inputCls} />
        </Field>

        <Field label="Study guide (markdown)">
          <textarea rows={12} value={form.studyGuide} onChange={e => { setField('studyGuide', e.target.value); }} className={`${inputCls} font-mono text-[13px]`} placeholder="# Title&#10;&#10;## Overview&#10;..." />
        </Field>

        {/* Quiz builder */}
        <div>
          <h2 className="stitch-font-headline text-[20px] text-[var(--stitch-primary)] uppercase mb-4">Quiz</h2>
          <div className="space-y-6">
            {quiz.map((q, qIdx) => (
              <div key={qIdx} className="border border-[var(--stitch-outline-variant)] rounded-xl p-5 bg-[var(--stitch-surface-container-lowest)]">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <span className="stitch-font-label text-[12px] text-[var(--stitch-on-surface-variant)] uppercase tracking-wider mt-2">Q{qIdx + 1}</span>
                  <textarea
                    rows={2}
                    value={q.question}
                    onChange={e => { updateQuestion(qIdx, 'question', e.target.value); }}
                    placeholder="Question text…"
                    className={`${inputCls} flex-1`}
                  />
                  <button
                    type="button"
                    onClick={() => { removeQuestion(qIdx); }}
                    disabled={quiz.length <= 1}
                    className="mt-1 p-1 text-red-400 hover:text-red-600 disabled:opacity-30 transition-colors"
                    title="Remove question"
                  >
                    <span className="material-symbols-outlined text-[20px]">delete</span>
                  </button>
                </div>
                <div className="space-y-2">
                  {q.options.map((opt, optIdx) => (
                    <div key={optIdx} className="flex items-center gap-3">
                      <input
                        type="radio"
                        name={`correct-${qIdx}`}
                        checked={q.correctIndex === optIdx}
                        onChange={() => { updateQuestion(qIdx, 'correctIndex', optIdx); }}
                        className="accent-[var(--stitch-primary)] shrink-0"
                        title={`Mark option ${String.fromCharCode(65 + optIdx)} as correct`}
                      />
                      <span className="stitch-font-label text-[12px] text-[var(--stitch-on-surface-variant)] w-4">{String.fromCharCode(65 + optIdx)}</span>
                      <input
                        type="text"
                        value={opt}
                        onChange={e => { updateOption(qIdx, optIdx, e.target.value); }}
                        placeholder={`Option ${String.fromCharCode(65 + optIdx)}`}
                        className={`${inputCls} flex-1`}
                      />
                    </div>
                  ))}
                </div>
                <p className="mt-2 stitch-font-body text-[11px] text-[var(--stitch-on-surface-variant)]">
                  Select the radio button next to the correct answer.
                </p>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addQuestion}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 border border-[var(--stitch-outline-variant)] rounded-lg stitch-font-label text-[12px] text-[var(--stitch-primary)] uppercase tracking-wider hover:bg-[var(--stitch-surface-container-low)] transition-colors"
          >
            <span className="material-symbols-outlined text-[16px]">add</span> Add question
          </button>
        </div>

        {/* Published toggle */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="published"
            checked={form.published}
            onChange={e => { setField('published', e.target.checked); }}
            className="accent-[var(--stitch-primary)] w-4 h-4"
          />
          <label htmlFor="published" className="stitch-font-body text-[14px] cursor-pointer">
            Published (visible to subscribers)
          </label>
        </div>

        {/* Save */}
        <div className="flex gap-3 pt-4 border-t border-[var(--stitch-outline-variant)]">
          <button
            type="button"
            onClick={() => { void save(); }}
            disabled={saving}
            className="px-8 py-4 bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)] rounded-full stitch-font-label text-[13px] uppercase tracking-widest font-bold hover:bg-[var(--stitch-primary-container)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : editId ? 'Save changes' : 'Create module'}
          </button>
          <Link
            href="/admin/modules"
            className="px-8 py-4 border border-[var(--stitch-outline-variant)] rounded-full stitch-font-label text-[13px] uppercase tracking-widest text-[var(--stitch-on-surface-variant)] hover:bg-[var(--stitch-surface-container-low)] transition-colors"
          >
            Cancel
          </Link>
        </div>
      </div>
    </div>
  );
}

// ── Shared helpers ────────────────────────────────────────────────────────────

const inputCls =
  'w-full px-3 py-2 bg-[var(--stitch-surface-container-lowest)] border border-[var(--stitch-outline-variant)] rounded-lg focus:ring-1 focus:ring-[var(--stitch-primary)] focus:border-[var(--stitch-primary)] outline-none stitch-font-body text-[14px] text-[var(--stitch-on-surface)]';

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="stitch-font-label text-[12px] text-[var(--stitch-on-surface-variant)] uppercase tracking-wider">{label}</label>
      {children}
      {hint && <p className="stitch-font-body text-[11px] text-[var(--stitch-on-surface-variant)]">{hint}</p>}
    </div>
  );
}

// ── Page exports ──────────────────────────────────────────────────────────────

export function AdminModuleNewPage(): React.ReactElement {
  return (
    <AppLayout currentRoute="/admin/modules">
      <AdminModuleFormContent />
    </AppLayout>
  );
}

export function AdminModuleEditPage(): React.ReactElement {
  const [, params] = useRoute<{ id: string }>('/admin/modules/:id/edit');
  const id = params?.id ?? '';
  return (
    <AppLayout currentRoute="/admin/modules">
      <AdminModuleFormContent editId={id} />
    </AppLayout>
  );
}
